// Accordion Component
// This file defines a reusable Accordion component with styled subcomponents for use in React applications.
// The Accordion allows toggling visibility of its content area, with customizable header alignment and optional icon.

import { useState } from "react";
import { styled } from "@stitches/react";
import { Button } from "@Forge/dist";

/**
 * AccordionContainer
 * Styled container for the entire Accordion component.
 * Arranges children in a vertical column with spacing.
 */
const AccordionContainer = styled("div", {
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: "16px",
  width: "100%",
});

/**
 * AccordionButtonWrapper
 * Styled wrapper for the header section (button and optional icon).
 * Supports alignment variants for header content.
 */
const AccordionButtonWrapper = styled("div", {
  display: "flex",
  alignItems: "center",
  gap: "8px",
  width: "100%",
  variants: {
    justifyContent: {
      start: {
        justifyContent: "flex-start",
      },
      end: {
        justifyContent: "flex-end",
      },
    },
  },
});

/**
 * AccordionContent
 * Styled content area for the Accordion.
 * Collapses/expands with a smooth height transition based on the isOpen prop.
 */
const AccordionContent = styled("div", {
  display: "flex",
  flexDirection: "column",
  gap: "16px",
  width: "100%",
  height: 0,
  overflow: "hidden",
  transition: "height 0.4s ease-in-out",
  variants: {
    isOpen: {
      true: {
        height: "max-content",
      },
    },
  },
});

/**
 * Accordion Component
 *
 * @param labelText - Object with 'open' and 'close' strings for button label
 * @param children - Content to display inside the Accordion when open
 * @param icon - Optional React node to display next to the button
 * @param justifyHeader - Alignment for the header section ('start' or 'end'), defaults to 'start'
 *
 * @returns A styled Accordion component with toggleable content area
 */
const Accordion = ({
  labelText,
  children,
  icon,
  justifyHeader = "start",
}: {
  labelText: {
    open: string;
    close: string;
  };
  children: React.ReactNode;
  icon?: React.ReactNode;
  justifyHeader?: "start" | "end";
}) => {
  // State to track whether the Accordion is open or closed
  const [isOpen, setIsOpen] = useState(false);

  return (
    <AccordionContainer data-testid="accordion-container">
      {/* Header section with optional icon and toggle button */}
      <AccordionButtonWrapper justifyContent={justifyHeader}>
        {icon ?? null}
        <Button
          data-testid="accordion-toggle-button"
          variant="tertiary"
          onClick={() => setIsOpen(!isOpen)}
        >
          {`${!isOpen ? labelText.open : labelText.close} ${
            isOpen ? "▲" : "▼"
          }`}
        </Button>
      </AccordionButtonWrapper>
      {/* Collapsible content area */}
      <AccordionContent data-testid="accordion-content" isOpen={isOpen}>
        {children}
      </AccordionContent>
    </AccordionContainer>
  );
};

export default Accordion;
